import { Divider, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import Decimal from "decimal.js";
import * as R from "ramda";
import { Proposal, ProposalStage } from "generated/graphql";
import useDeleteProposalProduct from "hooks/proposals/useDeleteProposalProduct";
import useCopyProposalProductToStage from "hooks/proposals/useCopyProposalProductToStage";
import useDuplicateProposalStage from "hooks/proposals/useDuplicateProposalStage";
import useUpdateProposalProductDiscount from "hooks/proposals/useUpdateProposalProductDiscount";
import useUpdateProposalProductOverrides from "hooks/proposals/useUpdateProposalProductOverrides";
import useUpdateProposalStageAddress from "hooks/proposals/useUpdateProposalStageAddress";
import useModalState from "hooks/useModalState";
import { toCents } from "utils/money/toCents";
import useUpdateProposalStageAddressAndClearProducts from "hooks/proposals/useUpdateProposalStageAddressAndClearProducts";
import { useCallback, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { schema, getDefaultValues } from "DDD/action-objects/ProposalStageUpdate";
import { CustomUpdateProposalStageMutationVariables } from "generated/graphql";
import humanReadableAddress from "utils/address/humanReadableAddress";
import MapModal from "modules/Mapmodal/MapModal";
import AddProductDrawer from "./AddProductDrawer";
import { useDraftContext } from "./DraftContext";
import ArrangeProductsDrawer from "./ArrangeProductsDrawer";
import EditableStageName from "./EditableStageName";
import StageMeasurementsModal from "./measurements/StageMeasurementsModal";
import StageProductTable from "./StageProductTable";
import { draftColors } from "./draftDesignTokens";

type StageProps = {
  proposalId: Proposal["id"];
  stage: ProposalStage;
  availableStages: ProposalStage[];
  isExpanded: boolean;
  initialExpandedProductId?: string | false;
  onToggleExpand: () => void;
  onRequestExpand: () => void;
  onExpandProduct: (stageId: string, productId: string) => void;
  onStageDuplicated: (stageId: string) => void;
  onDeleteStage?: (id: string) => void;
};

const parseDollars = (v: string) => {
  const trimmed = v.trim();
  return trimmed === "" ? null : toCents(parseFloat(trimmed));
};

function PendingStageForm({
  stage,
  onContinue,
}: {
  stage: ProposalStage;
  onContinue?: () => void;
}) {
  const [updateProposalStageAddress] = useUpdateProposalStageAddress();
  const mapModal = useModalState();

  const methods = useForm<CustomUpdateProposalStageMutationVariables & { address?: string }>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues({ id: stage.id }),
  });

  const address = methods.watch("address", "");
  const { errors } = methods.formState;

  const handleOnSubmit = useCallback(
    async (variables: CustomUpdateProposalStageMutationVariables & { address: string }) => {
      await updateProposalStageAddress(variables);
      onContinue?.();
    },
    [updateProposalStageAddress, onContinue]
  );

  return (
    <MDBox
      component="form"
      role="form"
      onSubmit={methods.handleSubmit(handleOnSubmit as any)}
      sx={{ px: "12px", pb: "16px" }}
    >
      <Grid container spacing="12px">
        <input type="hidden" {...methods.register("id")} />
        <Grid item xs={12} md={6}>
          <UiFieldGroup
            label="Job Name"
            value=""
            inputProps={methods.register("name")}
            placeholder="Enter Job Name"
          />
          {errors.name && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red, mt: "2px" }}>
              {errors.name.message}
            </UiTypography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="address"
            control={methods.control}
            render={({ field }) => {
              const displayAddress = field.value
                ? methods.getValues("addressOverride") || humanReadableAddress(field.value)
                : "";
              return (
                <>
                  <MDBox onClick={mapModal.onOpen} sx={{ cursor: "pointer" }}>
                    <UiFieldGroup label="Job Address" value="" readOnly>
                      <UiInput
                        fullWidth
                        value={displayAddress}
                        placeholder="Click to set address"
                        readOnly
                        inputProps={{ style: { cursor: "pointer" } }}
                      />
                    </UiFieldGroup>
                  </MDBox>
                  {errors.address && (
                    <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red, mt: "2px" }}>
                      {errors.address.message}
                    </UiTypography>
                  )}
                  <MapModal
                    open={mapModal.open}
                    onClose={mapModal.onClose}
                    guided
                    onSave={(data) => {
                      methods.setValue("mapData", JSON.stringify(data.geojson), {
                        shouldValidate: true,
                      });
                      methods.setValue("addressOverride", data.addressOverride);
                      field.onChange(JSON.stringify(data.address));
                    }}
                  />
                </>
              );
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <UiButton uiVariant="primary-blue" type="submit">
            Continue
          </UiButton>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default function Stage({
  proposalId,
  stage,
  availableStages,
  isExpanded,
  initialExpandedProductId = false,
  onToggleExpand,
  onRequestExpand,
  onExpandProduct,
  onStageDuplicated,
  onDeleteStage,
}: StageProps) {
  const [expandedProductId, setExpandedProductId] = useState<string | false>(
    initialExpandedProductId
  );
  const [deleteProposalProduct] = useDeleteProposalProduct();
  const [copyProposalProductToStage] = useCopyProposalProductToStage({ proposalId });
  const [
    duplicateProposalStage,
    { isSuccess: isDuplicateStageSuccess, getData: getDuplicatedStageData },
  ] = useDuplicateProposalStage({ proposalId });
  const [updateProductDiscount] = useUpdateProposalProductDiscount();
  const [updateProductOverrides] = useUpdateProposalProductOverrides();
  const [updateStageAddressAndClearProducts, updateStageAddressContext] =
    useUpdateProposalStageAddressAndClearProducts();
  const addProductDrawer = useModalState();
  const measureJobModal = useModalState();
  const arrangeProductsDrawer = useModalState();
  const editAddressModal = useModalState();

  const { isReadOnly } = useDraftContext();
  const isPending = !stage.name;
  const products = stage.proposalProducts ?? [];
  const hasAddress = !!(stage.address?.line1 || stage.address?.city);

  const stageAddress = [
    stage?.address?.line1,
    stage?.address?.city,
    stage?.address?.state,
    stage?.address?.zip,
  ]
    .filter(Boolean)
    .join(", ");

  const toggleProduct = useCallback((productId: string) => {
    setExpandedProductId((prev) => (prev === productId ? false : productId));
  }, []);

  const handleDeleteProduct = useCallback(
    async (productId: string) => {
      await deleteProposalProduct({ variables: { id: productId } });
    },
    [deleteProposalProduct]
  );

  const handleDuplicateStage = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      const result = await duplicateProposalStage({
        variables: { id: stage.id },
      });
      if (isDuplicateStageSuccess(result.data)) {
        const updatedProposal = getDuplicatedStageData(result) as Proposal | null;
        const lastStage = updatedProposal?.stages?.slice(-1)?.[0];
        if (lastStage?.id) {
          onStageDuplicated(lastStage.id);
        }
      }
    },
    [
      duplicateProposalStage,
      getDuplicatedStageData,
      isDuplicateStageSuccess,
      onStageDuplicated,
      stage.id,
    ]
  );

  const handleCopyProduct = useCallback(
    async (productId: string, targetStageId: string) => {
      const result = await copyProposalProductToStage({
        variables: {
          proposalProductId: productId,
          targetStageId,
        },
      });

      const updatedProposal = result.data?.copyProposalProductToStage;
      const updatedStage = updatedProposal?.stages?.find(
        (proposalStage) => proposalStage.id === targetStageId
      );
      const copiedProduct = updatedStage?.proposalProducts?.slice(-1)?.[0];

      if (updatedStage?.id && copiedProduct?.id) {
        if (updatedStage.id === stage.id) {
          setExpandedProductId(copiedProduct.id);
        }
        onExpandProduct(updatedStage.id, copiedProduct.id);
      }
    },
    [copyProposalProductToStage, onExpandProduct, stage.id]
  );

  const updateOverride = useCallback(
    (field: string, parse: (v: string) => any) => async (productId: string, value: string) => {
      await updateProductOverrides({
        variables: { proposalProductId: productId, [field]: parse(value) },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateDiscount = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const discount = trimmed === "" ? 0 : new Decimal(trimmed).times(100).round().toNumber();
      await updateProductDiscount({
        variables: { input: { id: productId, discount } },
      });
    },
    [updateProductDiscount]
  );

  const handleEditAddressSave = useCallback(
    async (data: any) => {
      const { line1, line2, city, state, zip, country, lat, lng } = data.address ?? {};
      const optionalFields = R.reject((v) => v == null || v === "", {
        addressLine2: line2,
        addressCity: city,
        addressState: state,
        addressZip: zip,
        addressCountry: country,
      });
      await updateStageAddressAndClearProducts({
        variables: {
          input: {
            proposalStageId: stage.id,
            addressLine1: line1,
            ...optionalFields,
            latitude: lat,
            longitude: lng,
            mapData: JSON.stringify(data.geojson),
            ...(data.addressOverride ? { addressOverride: data.addressOverride } : {}),
          },
        },
      });
      if (updateStageAddressContext.isSuccess) editAddressModal.onClose();
    },
    [stage.id, updateStageAddressAndClearProducts, updateStageAddressContext, editAddressModal]
  );

  const handlers = useMemo(
    () => ({
      onUpdateName: updateOverride("externalProductName", (v) => (v.trim() === "" ? null : v)),
      onUpdateProfit: updateOverride("profitOverride", parseDollars),
      onUpdateMargin: updateOverride("profitPercentageOverride", (v) => {
        const trimmed = v.trim();
        return trimmed === "" ? null : Math.max(0, Math.min(100, Math.round(parseFloat(trimmed))));
      }),
      onUpdateTotal: updateOverride("overallTotalOverride", parseDollars),
      onUpdateUnitPrice: updateOverride("unitPriceOverride", parseDollars),
      onUpdateJobSize: updateOverride("jobSize", (v) => Math.round(Number(v)) || null),
      onUpdateDiscount: handleUpdateDiscount,
    }),
    [updateOverride, handleUpdateDiscount]
  );

  return (
    <UiCard variant="panel">
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p="12px"
        gap="24px"
        sx={{
          cursor: isPending ? "default" : "pointer",
          "&:hover": isPending ? {} : { backgroundColor: "rgba(0,0,0,0.01)" },
        }}
        onClick={isPending ? undefined : onToggleExpand}
      >
        <MDBox display="flex" alignItems="center" gap="8px">
          {!isPending && (
            <Icon sx={{ color: draftColors.textSecondary, transition: "transform 0.2s" }}>
              {isExpanded ? "expand_more" : "chevron_right"}
            </Icon>
          )}
          <MDBox display="flex" flexDirection="column" gap="2px">
            <MDBox display="flex" alignItems="center" gap="4px">
              <EditableStageName stage={stage} />
            </MDBox>
            {stageAddress && (
              <MDBox display="flex" alignItems="center" gap="4px">
                <UiTypography uiVariant="caption">
                  {stage.addressOverride ?? stageAddress}
                </UiTypography>
                {!isReadOnly && (
                  <Icon
                    sx={{
                      fontSize: "14px !important",
                      color: draftColors.textSecondary,
                      cursor: "pointer",
                      "&:hover": { color: draftColors.blue },
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      editAddressModal.onOpen();
                    }}
                  >
                    edit
                  </Icon>
                )}
              </MDBox>
            )}
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center" gap="16px">
          {!isPending && hasAddress && !isReadOnly && (
            <UiButton
              uiVariant="outlined"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (!isExpanded) onRequestExpand();
                measureJobModal.onOpen();
              }}
            >
              Measure Job
            </UiButton>
          )}
          {!isPending && hasAddress && !isReadOnly && (
            <UiButton
              uiVariant="primary-blue"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (!isExpanded) onRequestExpand();
                addProductDrawer.onOpen();
              }}
            >
              Add Product
            </UiButton>
          )}
          {!isPending && hasAddress && (
            <UiButton uiVariant="outlined" onClick={handleDuplicateStage}>
              Duplicate Job
            </UiButton>
          )}
          {onDeleteStage && !isReadOnly && (
            <MDBox
              sx={{ color: draftColors.red, cursor: "pointer", display: "flex" }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDeleteStage(stage.id);
              }}
            >
              <Icon fontSize="small">delete</Icon>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      {isPending && <PendingStageForm stage={stage} onContinue={addProductDrawer.onOpen} />}

      {!isPending && isExpanded && (
        <>
          {products.length > 0 ? (
            <>
              <StageProductTable
                products={products}
                stage={stage}
                expandedProductId={expandedProductId}
                onToggleExpand={toggleProduct}
                onDeleteProduct={handleDeleteProduct}
                onCopyProduct={handleCopyProduct}
                availableStages={availableStages}
                {...handlers}
              />
              <MDBox px="12px" py="8px" display="flex" justifyContent="flex-end">
                <UiButton
                  uiVariant="outlined"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    arrangeProductsDrawer.onOpen();
                  }}
                >
                  Arrange Products
                </UiButton>
              </MDBox>
              <Divider sx={{ borderColor: draftColors.border }} />
            </>
          ) : (
            <MDBox
              sx={{
                borderTop: `1px solid ${draftColors.border}`,
                px: "24px",
                py: "32px",
                textAlign: "center",
              }}
            >
              <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
                No products yet. Click &ldquo;Add product&rdquo; to get started.
              </UiTypography>
            </MDBox>
          )}
        </>
      )}

      <AddProductDrawer
        open={addProductDrawer.open}
        onClose={addProductDrawer.onClose}
        stage={stage}
        onSuccess={(updatedProposal) => {
          if (!isExpanded) onRequestExpand();
          const updatedStage = updatedProposal?.stages?.find((s: any) => s.id === stage.id);
          const lastProduct = updatedStage?.proposalProducts?.slice(-1)?.[0];
          if (lastProduct?.id) {
            setExpandedProductId(lastProduct.id);
          }
        }}
      />
      <StageMeasurementsModal
        open={measureJobModal.open}
        onClose={measureJobModal.onClose}
        stage={stage}
        onSuccess={(updatedProposal) => {
          if (!isExpanded) onRequestExpand();
          const updatedStage = updatedProposal?.stages?.find((s: any) => s.id === stage.id);
          const lastProduct = updatedStage?.proposalProducts?.slice(-1)?.[0];
          if (lastProduct?.id) {
            setExpandedProductId(lastProduct.id);
          }
        }}
      />

      {!isPending && (
        <MapModal
          open={editAddressModal.open}
          onClose={editAddressModal.onClose}
          guided
          onSave={handleEditAddressSave}
        />
      )}
      <ArrangeProductsDrawer
        open={arrangeProductsDrawer.open}
        onClose={arrangeProductsDrawer.onClose}
        stage={stage}
      />
    </UiCard>
  );
}
